import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ManageUsersActionType,
  useManageUsersContext,
} from "../../../../context/ManageUsersContext";
import useAlert from "../../../../hooks/useAlert";
import { Permissions } from "../../../../models/permission";
import Role from "../../../../models/role";
import {
  IUser,
  UserDetailsFormInputs,
} from "../../../../models/users.interface";
import authorizationService from "../../../../services/authorization.service";
import usersService from "../../../../services/users.service";
import AlertModal from "../../../Shared/AlertModal/AlertModal";
import EditUser from "../../EditUser/EditUser";
import SecurityDetails from "../../EditUser/SecurityDetails/SecurityDetails";
import ProGeneralDetails from "../ProGeneralDetails/ProGeneralDetails";

const ProUserDetailsPortal = () => {
  const [domReady, setDomReady] = useState(false);

  useEffect(() => {
    setDomReady(true);
  }, []);

  const {
    state: { loading, selectedUser },
    actions: { loadUsers },
    dispatch,
  } = useManageUsersContext();

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const userUpdatedHandler = async () => {
    await loadUsers();
  };

  const cancelUserSelectionHandler = () => {
    dispatch({
      type: ManageUsersActionType.SET_SELECTED_USER,
      payload: null,
    });
  };

  const toggleUserActive = async (isActive: boolean) => {
    if (!selectedUser) {
      return;
    }
    const { success, errorCode } = await usersService.updateUserState(
      selectedUser.id,
      isActive
    );

    if (!success) {
      const { message, support } = getAlertMessageAndSupportContent(
        isActive ? "ERROR.ACTIVATE_USER" : "ERROR.DEACTIVATE_USER",
        `An error occurred while trying to 
      ${
        isActive ? "activate" : "deactivate"
      } the user.<br></br>Please try again.`,
        {
          br: () => <br></br>,
        },
        errorCode
      );
      showErrorAlert(message, support);
      return;
    }

    await loadUsers();
  };

  const container = document.getElementById("userInfoWrapper");

  let formData;
  if (selectedUser) formData = mapToUserDetailsForm(selectedUser);

  return domReady && container
    ? createPortal(
        <>
          <EditUser
            isUserSelected={!loading && !!selectedUser}
            detailsTabContent={
              <ProGeneralDetails
                formData={formData}
                userStatus={selectedUser?.status}
                userId={selectedUser?.id ?? ""}
                activeProfilesCount={selectedUser?.activeProfilesCount}
                totalProfiles={selectedUser?.totalProfiles}
                createdAt={selectedUser?.createdAt}
                activatedAt={selectedUser?.activatedAt}
                loggedAt={selectedUser?.lastLoginDate}
                onUpdated={userUpdatedHandler}
                onCancel={cancelUserSelectionHandler}
                onToggleUserActive={toggleUserActive}
              />
            }
            securityTabContent={
              <SecurityDetails
                userId={selectedUser?.id ?? ""}
                userEmail={selectedUser?.email ?? "N/A"}
                userStatus={selectedUser?.status ?? "disabled"}
                userType={"professional"}
                onUpdated={userUpdatedHandler}
              />
            }
          />
          <AlertModal {...alertProps}>{alertMessage}</AlertModal>
        </>,
        container
      )
    : null;
};

export default ProUserDetailsPortal;

const mapToUserDetailsForm = (user: IUser): UserDetailsFormInputs => {
  const {
    email,
    firstName,
    lastName,
    phone,
    organization,
    permissions,
    role,
    salutation,
  } = user;

  return {
    entity: organization?.id ?? "",
    firstName: firstName,
    lastName: lastName,
    loginName: email?.split("@")[0],
    emailDomain: email?.split("@")[1],
    title: salutation,
    email: email ?? "",
    phone: phone,
    isAdmin: role === Role.OrgAdmin,
    canManageProUsers: authorizationService.hasPermissions(permissions ?? [], [
      Permissions.CanManageProUsers,
    ]),
    canLogIntoPortal: authorizationService.hasPermissions(permissions ?? [], [
      Permissions.CanLogIntoPortal,
    ]),
    canViewEcgs: authorizationService.hasPermissions(permissions ?? [], [
      Permissions.CanViewEcgs,
    ]),
    canEditInterpretation: authorizationService.hasPermissions(
      permissions ?? [],
      [Permissions.CanEditInterpretation]
    ),
    canManageNonProUsers: authorizationService.hasPermissions(
      permissions ?? [],
      [Permissions.CanManageNonProUsers]
    ),
    canViewReports: authorizationService.hasPermissions(permissions ?? [], [
      Permissions.CanViewReports,
    ]),
    canApproveEcg: authorizationService.hasPermissions(permissions ?? [], [
      Permissions.CanApproveEcg,
    ]),
    canSendEcgsViaMail: authorizationService.hasPermissions(permissions ?? [], [
      Permissions.CanSendEcgsViaMail,
    ]),
    canViewSelfClinicsEcgs: authorizationService.hasPermissions(
      permissions ?? [],
      [Permissions.CanViewSelfClinicsEcgs]
    ),
    canArchiveEcgs: authorizationService.hasPermissions(permissions ?? [], [
      Permissions.CanArchiveEcgs,
    ]),
  };
};
